import { gql } from '@/utilities/gql';
import type { VariablesOf, ResultOf } from '@/utilities/gql';
import type { QueryFunctionContext } from '@tanstack/react-query';
import { gqlClient } from '@/utilities/graphQLClient';
import { subYears, format, subDays } from 'date-fns';

const QUERY = gql(`
  query getCompanyDividends(
    $id: String
    $startDate: DateTime!
    $endDate: DateTime!
  ) {
    Company(id: $id) {
      name
      listingCurrencyISO
      dividends(dateFrom: $startDate, dateTo: $endDate) {
        exDate
        payDate
        recordDate
        divAmount
        divAmountListingCurrency
        currencyIsoCode
        dividendTypeName
        divFreqTypeName
        annualizedDividend
        annualizedDividendListingCurrency
        annualizedYield
      }
    }
  }
`);

type Args = VariablesOf<typeof QUERY>;

// Exported for solo-charts
// TODO: Update solo-charts to using TADA so it can rely on its own typing
// See: /packages/solo-charts/src/getHistoricDividend.tsx
export type Result = ResultOf<typeof QUERY>;

type QueryKeyType = [string, Args['id']];

const DIVIDENDS_TIMESERIES_QUERY_KEY = 'dividends-timeseries';

export const getDividendsTimeseriesDataKey = (id: string): QueryKeyType => [
  DIVIDENDS_TIMESERIES_QUERY_KEY,
  id,
];

export const getPeriodFromNow = () => {
  const now = new Date();
  const PAST_TEN_YEAR = 10;
  const PAST_ONE_DAY = 1;

  const endDate = format(subDays(now, PAST_ONE_DAY), 'yyyy-MM-dd');
  const startDate = format(subYears(now, PAST_TEN_YEAR), 'yyyy-MM-dd');
  return { startDate, endDate };
};

export const queryFn = async ({
  queryKey: [_, id],
}: QueryFunctionContext<QueryKeyType>) => {
  const { startDate, endDate } = getPeriodFromNow();

  const response = await gqlClient.request(QUERY, {
    id,
    startDate,
    endDate,
  });

  if ('Company' in response) {
    return response.Company;
  }
};
